import { useEffect } from "react";
import { useLocation } from "react-router-dom";

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalPath?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: "website" | "article";
  articlePublishedTime?: string;
  articleModifiedTime?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const SITE_URL = (() => {
  try {
    if (typeof import.meta !== "undefined" && import.meta.env && import.meta.env.VITE_SITE_URL) {
      return import.meta.env.VITE_SITE_URL as string;
    }
  } catch {
    // env not available
  }
  return "https://sugargoo.cfd";
})();

function setMeta(id: string, name: string, content: string, isProperty: boolean): void {
  const attr = isProperty ? "property" : "name";
  let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function removeMeta(name: string, isProperty: boolean): void {
  const attr = isProperty ? "property" : "name";
  const el = document.querySelector(`meta[${attr}="${name}"]`);
  if (el) el.remove();
}

function setLink(rel: string, href: string): void {
  let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export default function SEO({
  title,
  description,
  keywords,
  canonicalPath,
  ogTitle,
  ogDescription,
  ogType = "website",
  articlePublishedTime,
  articleModifiedTime,
  jsonLd,
}: SEOProps) {
  const location = useLocation();
  const canonical = canonicalPath ? `${SITE_URL}${canonicalPath}` : `${SITE_URL}${location.pathname}`;

  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;

    setMeta("description", "description", description, false);
    if (keywords) setMeta("keywords", "keywords", keywords, false);
    setLink("canonical", canonical);

    const ogTitleVal = ogTitle || title;
    const ogDescVal = ogDescription || description;
    setMeta("og:title", "og:title", ogTitleVal, true);
    setMeta("og:description", "og:description", ogDescVal, true);
    setMeta("og:url", "og:url", canonical, true);
    setMeta("og:type", "og:type", ogType, true);

    setMeta("twitter:card", "twitter:card", "summary_large_image", false);
    setMeta("twitter:title", "twitter:title", ogTitleVal, false);
    setMeta("twitter:description", "twitter:description", ogDescVal, false);

    if (articlePublishedTime) {
      setMeta("article:published_time", "article:published_time", articlePublishedTime, true);
    }
    if (articleModifiedTime) {
      setMeta("article:modified_time", "article:modified_time", articleModifiedTime, true);
    }

    const jsonLdArray = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
    const scriptIds: string[] = [];

    jsonLdArray.forEach((ld, i) => {
      const scriptId = `jsonld-seo-${i}`;
      scriptIds.push(scriptId);
      let script = document.getElementById(scriptId) as HTMLScriptElement | null;
      if (!script) {
        script = document.createElement("script");
        script.id = scriptId;
        script.type = "application/ld+json";
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(ld);
    });

    return () => {
      document.title = prevTitle;
      removeMeta("og:title", true);
      removeMeta("og:description", true);
      removeMeta("og:url", true);
      removeMeta("og:type", true);
      if (articlePublishedTime) removeMeta("article:published_time", true);
      if (articleModifiedTime) removeMeta("article:modified_time", true);
      scriptIds.forEach((id) => {
        const s = document.getElementById(id);
        if (s) s.remove();
      });
    };
  }, [title, description, keywords, canonical, ogTitle, ogDescription, ogType, articlePublishedTime, articleModifiedTime, jsonLd]);

  return null;
}

export { SITE_URL };